import { useEffect, useMemo, useRef } from 'react';

import { getEmptyCustomFields, useLazyCustomFields } from 'utils/customFields';
import { useCurrentUserInAcw } from 'utils/user';

type UseConversationStateOptions = {
  /** The conversation id */
  readonly csid: number;
};

/**
 * A hook for determining whether a conversation assigned to current agent has
 * missing required custom fields when the agent is in after conversation work
 * (ACW) for said conversation.
 */
export const useConversationHasMissingRequiredCustomFieldsInAcw = ({
  csid,
}: UseConversationStateOptions): boolean => {
  const hasMissingRequiredCustomFieldsInAcw = useRef(false);
  const isInAcw = useCurrentUserInAcw({ csid });

  const [readCustomFields, { asyncStatus, customFields }] =
    useLazyCustomFields();

  // Handles reading custom fields.
  useEffect(() => {
    if (isInAcw) {
      readCustomFields({
        entityId: 'conversation',
        entityInstanceId: `${csid}`,
      });
    }
  }, [csid, isInAcw, readCustomFields]);

  const emptyRequiredCustomFields = useMemo(
    () => getEmptyCustomFields({ customFields, onlyRequired: true }),
    [customFields],
  );

  return useMemo(() => {
    if (!isInAcw) {
      return false;
    }

    if (asyncStatus !== 'fulfilled') {
      return hasMissingRequiredCustomFieldsInAcw.current;
    }

    hasMissingRequiredCustomFieldsInAcw.current =
      !!emptyRequiredCustomFields.length;

    return hasMissingRequiredCustomFieldsInAcw.current;
  }, [isInAcw, asyncStatus, emptyRequiredCustomFields.length]);
};
